/**
 * Batch Context - State management for batch operations
 *
 * Provides centralized state management for all batch-related operations
 * including creating, loading, filtering, and preloading solar data.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { batchService } from '../services/batchService';
import { useAuth } from './AuthContext';
import {
  Batch,
  BatchFilters,
  CreateBatchRequest,
  PreloadSolarRequest,
  PreloadSolarResponse,
  BatchProgressResponse,
  CSVRow,
} from '../types/batch';

interface BatchContextType {
  // State
  batches: Batch[];
  currentBatch: Batch | null;
  batchProgress: BatchProgressResponse | null;
  isLoading: boolean;
  error: string | null;
  filters: BatchFilters;

  // Preload state
  isPreloading: boolean;
  preloadProgress: number;
  preloadStatus: string | null;

  // Actions
  loadBatches: () => Promise<void>;
  loadBatchDetails: (batchId: string) => Promise<void>;
  loadBatchProgress: (batchId: string) => Promise<void>;
  createBatch: (batchName: string, csvContent: string) => Promise<Batch & { solar_preload_started?: boolean }>;
  deleteBatch: (batchId: string) => Promise<void>;
  updateFilters: (filters: Partial<BatchFilters>) => void;
  clearFilters: () => void;
  preloadSolar: (batchId: string, mode: 'parallel' | 'series') => Promise<PreloadSolarResponse | undefined>;
  updateLocationStatus: (
    batchId: string,
    dealId: string,
    status: 'pending' | 'in_progress' | 'completed' | 'not_found'
  ) => Promise<void>;
  setCurrentBatch: (batch: Batch | null) => void;
  clearError: () => void;
}

const BatchContext = createContext<BatchContextType | undefined>(undefined);

export const BatchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();

  // State
  const [batches, setBatches] = useState<Batch[]>([]);
  const [currentBatch, setCurrentBatch] = useState<Batch | null>(null);
  const [batchProgress, setBatchProgress] = useState<BatchProgressResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<BatchFilters>({
    status: 'all',
  });

  // Preload state
  const [isPreloading, setIsPreloading] = useState(false);
  const [preloadProgress, setPreloadProgress] = useState(0);
  const [preloadStatus, setPreloadStatus] = useState<string | null>(null);

  /**
   * Get auth token from localStorage
   */
  const getAuthToken = (): string | null => {
    return localStorage.getItem('authToken');
  };

  /**
   * Load all batches with current filters
   */
  const loadBatches = useCallback(async () => {
    const authToken = getAuthToken();
    if (!authToken) {
      setError('Not authenticated');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const fetchedBatches = await batchService.getBatches(filters, authToken);
      setBatches(fetchedBatches);
    } catch (err: any) {
      setError(err.message || 'Failed to load batches');
      console.error('Error loading batches:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  /**
   * Load single batch details
   */
  const loadBatchDetails = useCallback(
    async (batchId: string) => {
      const authToken = getAuthToken();
      if (!authToken) {
        setError('Not authenticated');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const batch = await batchService.getBatchDetails(batchId, authToken);
        setCurrentBatch(batch);
      } catch (err: any) {
        setError(err.message || 'Failed to load batch details');
        console.error('Error loading batch details:', err);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Load batch progress statistics
   */
  const loadBatchProgress = useCallback(
    async (batchId: string) => {
      const authToken = getAuthToken();
      if (!authToken) {
        setError('Not authenticated');
        return;
      }

      try {
        const progress = await batchService.getBatchProgress(batchId, authToken);
        setBatchProgress(progress);
      } catch (err: any) {
        setError(err.message || 'Failed to load batch progress');
        console.error('Error loading batch progress:', err);
      }
    },
    []
  );

  /**
   * Create new batch from CSV
   */
  const createBatch = useCallback(
    async (batchName: string, csvContent: string): Promise<Batch & { solar_preload_started?: boolean }> => {
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error('Not authenticated');
      }

      setIsLoading(true);
      setError(null);

      try {
        const request: CreateBatchRequest = {
          batch_name: batchName,
          csv_content: csvContent,
        };

        const response = await batchService.createBatch(request, authToken);

        // Reload batches after creation
        await loadBatches();

        // Return the created batch (fetch it to get full details)
        const createdBatch = batches.find((b) => b.batch_id === response.batch_id);
        if (!createdBatch) {
          // If not found in cache, fetch it
          await loadBatchDetails(response.batch_id);
          return { ...currentBatch!, solar_preload_started: response.solar_preload_started };
        }

        return { ...createdBatch, solar_preload_started: response.solar_preload_started };
      } catch (err: any) {
        setError(err.message || 'Failed to create batch');
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [batches, currentBatch, loadBatches, loadBatchDetails]
  );

  /**
   * Delete batch
   */
  const deleteBatch = useCallback(
    async (batchId: string) => {
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error('Not authenticated');
      }

      setIsLoading(true);
      setError(null);

      try {
        await batchService.deleteBatch(batchId, authToken);

        // Remove from local state
        setBatches((prev) => prev.filter((b) => b.batch_id !== batchId));

        // Clear current batch if it was deleted
        if (currentBatch?.batch_id === batchId) {
          setCurrentBatch(null);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to delete batch');
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [currentBatch]
  );

  /**
   * Update filters
   */
  const updateFilters = useCallback((newFilters: Partial<BatchFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters({ status: 'all' });
  }, []);

  /**
   * Pre-load solar data for all locations in batch
   */
  const preloadSolar = useCallback(
    async (batchId: string, mode: 'parallel' | 'series') => {
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error('Not authenticated');
      }

      setIsPreloading(true);
      setPreloadProgress(0);
      setPreloadStatus('Starting solar data preload...');
      setError(null);

      try {
        const request: PreloadSolarRequest = {
          mode,
          delay_ms: mode === 'series' ? 500 : undefined,
        };

        const response = await batchService.preloadSolar(batchId, request, authToken);

        setPreloadProgress(100);
        setPreloadStatus(
          `Completed! ${response.results.successful}/${response.results.total} locations processed`
        );

        // Reload batch details to get updated solar data
        await loadBatchDetails(batchId);

        return response;
      } catch (err: any) {
        setError(err.message || 'Failed to preload solar data');
        setPreloadStatus('Failed to preload solar data');
        throw err;
      } finally {
        setIsPreloading(false);
      }
    },
    [loadBatchDetails]
  );

  /**
   * Update location status
   */
  const updateLocationStatus = useCallback(
    async (
      batchId: string,
      dealId: string,
      status: 'pending' | 'in_progress' | 'completed' | 'not_found'
    ) => {
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error('Not authenticated');
      }

      try {
        await batchService.updateLocationStatus(batchId, dealId, status, authToken);

        // Update local state
        if (currentBatch?.batch_id === batchId) {
          setCurrentBatch((prev) => {
            if (!prev) return prev;

            return {
              ...prev,
              locations: prev.locations.map((loc) =>
                loc.deal_id === dealId ? { ...loc, status } : loc
              ),
            };
          });
        }

        // Reload progress
        await loadBatchProgress(batchId);
      } catch (err: any) {
        setError(err.message || 'Failed to update location status');
        throw err;
      }
    },
    [currentBatch, loadBatchProgress]
  );

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Auto-load batches when filters change
  useEffect(() => {
    const authToken = getAuthToken();
    if (authToken) {
      loadBatches();
    }
  }, [filters, loadBatches]);

  const value: BatchContextType = {
    // State
    batches,
    currentBatch,
    batchProgress,
    isLoading,
    error,
    filters,

    // Preload state
    isPreloading,
    preloadProgress,
    preloadStatus,

    // Actions
    loadBatches,
    loadBatchDetails,
    loadBatchProgress,
    createBatch,
    deleteBatch,
    updateFilters,
    clearFilters,
    preloadSolar,
    updateLocationStatus,
    setCurrentBatch,
    clearError,
  };

  return <BatchContext.Provider value={value}>{children}</BatchContext.Provider>;
};

/**
 * Hook to use batch context
 */
export const useBatch = () => {
  const context = useContext(BatchContext);
  if (!context) {
    throw new Error('useBatch must be used within a BatchProvider');
  }
  return context;
};
